import React, { useState } from "react";
import { api } from "@/lib/api";
import { PublicLayout } from "@/components/site/PublicLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { BRAND } from "@/constants/brand";
import { toast } from "sonner";
import { Mail, Phone, MapPin } from "lucide-react";

const fieldClass =
  "mt-1 h-11 rounded-none border-2 border-[color:var(--coh-ink)] bg-[color:var(--coh-paper-2)] text-[color:var(--coh-ink)] focus-visible:ring-0";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/public/contact", form);
      setSent(true);
      toast.success("Message received", { description: r.data?.message || "Thanks \u2014 we received your note." });
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Could not send", { description: err?.response?.data?.detail || "Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLayout>
      <section className="hero-wash">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 pt-16 pb-16">
          <div className="kicker">Contact</div>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold tracking-[-0.03em] text-[color:var(--coh-ink)]">Get in touch</h1>
          <p className="mt-4 text-[color:var(--coh-ink-muted)] max-w-2xl">
            For support, complaints, privacy questions, or questions about our Terms — send a note
            below or reach us directly.
          </p>

          <div className="mt-10 grid gap-8 lg:grid-cols-[1fr,1.1fr] items-start">
            {/* Direct contact details */}
            <div className="nb-box p-6 sm:p-7" data-testid="contact-details">
              <div className="kicker">{BRAND.entity}</div>
              <ul className="mt-5 space-y-5 text-sm">
                <li className="flex items-start gap-3">
                  <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center border-2 border-[color:var(--coh-ink)] bg-[color:var(--coh-blue)] text-[color:var(--coh-on-accent)]"><Mail className="h-4 w-4" /></span>
                  <span>
                    <span className="block font-mono text-[11px] uppercase tracking-[0.14em] text-[color:var(--coh-ink-muted)]">Email</span>
                    <a href={BRAND.emailHref} data-testid="contact-email-link" className="font-semibold text-[color:var(--coh-ink)] underline underline-offset-4 decoration-2 hover:text-[color:var(--coh-blue)]">{BRAND.email}</a>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center border-2 border-[color:var(--coh-ink)] bg-[color:var(--coh-blue)] text-[color:var(--coh-on-accent)]"><Phone className="h-4 w-4" /></span>
                  <span>
                    <span className="block font-mono text-[11px] uppercase tracking-[0.14em] text-[color:var(--coh-ink-muted)]">Phone</span>
                    <a href={BRAND.phoneHref} data-testid="contact-phone-link" className="font-semibold text-[color:var(--coh-ink)] underline underline-offset-4 decoration-2 hover:text-[color:var(--coh-blue)]">{BRAND.phone}</a>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center border-2 border-[color:var(--coh-ink)] bg-[color:var(--coh-blue)] text-[color:var(--coh-on-accent)]"><MapPin className="h-4 w-4" /></span>
                  <span>
                    <span className="block font-mono text-[11px] uppercase tracking-[0.14em] text-[color:var(--coh-ink-muted)]">Mail</span>
                    <span className="font-semibold text-[color:var(--coh-ink)]" data-testid="contact-address">
                      {BRAND.entity}<br />
                      {BRAND.address.line1}<br />
                      {BRAND.address.line2}<br />
                      {BRAND.address.country}
                    </span>
                  </span>
                </li>
              </ul>
            </div>

            {/* Message form */}
            <div className="nb-box p-6 sm:p-8">
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <div>
                  <label className="text-sm font-semibold text-[color:var(--coh-ink)]">Name</label>
                  <Input required data-testid="contact-name-input" value={form.name} onChange={(e) => upd("name", e.target.value)} className={fieldClass} />
                </div>
                <div>
                  <label className="text-sm font-semibold text-[color:var(--coh-ink)]">Email</label>
                  <Input required type="email" data-testid="contact-email-input" value={form.email} onChange={(e) => upd("email", e.target.value)} className={fieldClass} />
                </div>
                <div>
                  <label className="text-sm font-semibold text-[color:var(--coh-ink)]">Message</label>
                  <Textarea required data-testid="contact-message-input" value={form.message} onChange={(e) => upd("message", e.target.value)} rows={6} className="mt-1 rounded-none border-2 border-[color:var(--coh-ink)] bg-[color:var(--coh-paper-2)] text-[color:var(--coh-ink)] focus-visible:ring-0" />
                </div>
                <Button type="submit" disabled={loading} data-testid="contact-submit-button" className="btn-nb btn-nb-primary h-11 px-6">
                  {loading ? "Sending\u2026" : "Send message"}
                </Button>
                {sent && <p role="status" className="text-sm font-semibold text-[color:var(--coh-blue)]">Thanks — we received your note.</p>}
              </form>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
